/*
 * Figma `48:34` — Feedback State Panel. State: Loading, Empty, Error, Permission (+ Filtered-empty).
 *
 * Five states, not four, and the extra one is the point. "No orders yet" and "no orders match this
 * filter" are opposite situations: one means the work has not started, the other means you are
 * looking through the wrong window and there is a control on screen to change it. A queue that
 * answers both with the same sentence sends someone hunting for data that was never missing.
 *
 * Loading is a skeleton shaped like the thing that is coming rather than a spinner in a collapsed
 * box, so the page does not jump when the data lands.
 */
import type { ReactNode } from 'react';
import { IconEmpty, IconSearch, IconDanger, IconLock, IconRetry } from './icons';
import { Button } from './Button';

export type FeedbackKind = 'loading' | 'empty' | 'filteredEmpty' | 'error' | 'permission';

export function LoadingRows({ rows = 5, label = 'กำลังโหลด' }: { rows?: number; label?: string }) {
  return (
    <div className="jt-skeleton-rows" role="status" aria-live="polite" aria-busy="true">
      <span className="jt-sr-only">{label}</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="jt-skeleton jt-skeleton-row" aria-hidden="true" />
      ))}
    </div>
  );
}

export function FeedbackState({ state, title, children, action, onRetry, onClearFilters }: {
  state: Exclude<FeedbackKind, 'loading'>;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  /** Error only — the recovery the panel offers instead of asking for a page reload. */
  onRetry?: () => void;
  /** Filtered-empty only — the way back out of the window you are looking through. */
  onClearFilters?: () => void;
}) {
  const Glyph = state === 'error' ? IconDanger
    : state === 'permission' ? IconLock
      : state === 'filteredEmpty' ? IconSearch : IconEmpty;

  return (
    <div
      className="jt-feedback"
      data-state={state}
      /* An error is announced; an empty list is just what the page says. */
      role={state === 'error' ? 'alert' : 'status'}
    >
      <span className="jt-feedback-icon"><Glyph size={20} /></span>
      <div className="jt-feedback-title">{title}</div>
      {children && <div className="jt-feedback-text">{children}</div>}
      {onClearFilters && state === 'filteredEmpty' && (
        <Button variant="secondary" size="sm" onClick={onClearFilters}>ล้างตัวกรอง</Button>
      )}
      {onRetry && state === 'error' && (
        <Button variant="secondary" size="sm" icon={<IconRetry size={15} />} onClick={onRetry}>ลองอีกครั้ง</Button>
      )}
      {action}
    </div>
  );
}

/**
 * The permission panel, with its own copy.
 *
 * It names the screen and the role, because "คุณไม่มีสิทธิ์" on its own leaves someone unable to
 * tell whether they asked for the wrong thing or hold the wrong token — and the second one is
 * fixable by a person who knows which it is.
 */
export function PermissionDenied({ screen, role }: { screen: string; role?: string | null }) {
  return (
    <FeedbackState state="permission" title={`Token นี้เปิด “${screen}” ไม่ได้`}>
      {role
        ? `สิทธิ์ปัจจุบันคือ ${role} — หน้านี้ต้องใช้สิทธิ์ที่สูงกว่า ขอ Token ใหม่จากเจ้าของงานหากจำเป็นต้องเข้าถึง`
        : 'ระบบยังไม่ทราบสิทธิ์ของ Token นี้ — ลองโหลดใหม่อีกครั้ง หากยังเหมือนเดิมให้ขอ Token ใหม่จากเจ้าของงาน'}
    </FeedbackState>
  );
}
